<script lang="ts">
    import {getSanitize} from 'src/core';
    import type {Auth} from 'src/typings';
    import type {LoginView} from 'shared/login/kp-login/types';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import {exists} from 'shared/utils/custom-utils';
    import {LoginSystemIdToComponentMap} from 'shared/login/kp-login/login-systems/login-system-id-to-component.map';
    import LoginSystemContextProvider from 'shared/login/kp-login/LoginSystemContextProvider.svelte';
    import {setLoginFormContext} from 'shared/login/kp-login/context';
    import {repeatedClick} from 'shared/svelte-actions';

    export let loginView: LoginView;
    export let onLoginSuccess: (auth: Auth) => void;
    export let onLoginCancel: () => void;

    const REVEAL_CLICK_COUNT = 5;
    const REVEAL_WITHIN_MS = 3000;
    const NOT_COUNTED_CLICK_TARGETS = 'input, select, textarea, button, a, label';

    let hiddenAuthSystemsRevealed = false;

    $: visibleAuthSystems = loginView.authSystemViews.filter((authSystem) => authSystem.showingInMainLogin || hiddenAuthSystemsRevealed);

    const sanitize = getSanitize();
    const {state$} = setLoginFormContext();

    $: errorMessage = $state$?.errorMessage ?? null;
    $: showProcessing = $state$?.status === 'PROCESSING';

    function revealHiddenAuthSystems() {
        hiddenAuthSystemsRevealed = true;
    }
</script>


<div class="login-container"
     use:repeatedClick={{count: REVEAL_CLICK_COUNT, withinMs: REVEAL_WITHIN_MS, ignoredTargetsSelector: NOT_COUNTED_CLICK_TARGETS, onRepeatedClick: revealHiddenAuthSystems}}>
    <div class="auth-systems">
        {#each visibleAuthSystems as authSystem(authSystem.id)}
            <div class="auth-system {authSystem.id}">
                <LoginSystemContextProvider {loginView} authSystemView={authSystem} {onLoginSuccess} {onLoginCancel}>
                    <svelte:component this={LoginSystemIdToComponentMap[authSystem.id]}/>
                </LoginSystemContextProvider>
            </div>
        {/each}
    </div>

    {#if showProcessing}
        <KpLoadingBlock size="xs"/>
    {/if}

    {#if (exists(errorMessage))}
        <div class="login-error" role="alert">
            {@html sanitize(errorMessage)}
        </div>
    {/if}
</div>

<style lang="less">
    @import (reference) "styles/portaro.media-queries.less";

    .login-container {
        touch-action: manipulation; // the reveal taps must not zoom the page on phones

        .login-error {
            margin-top: 10px;
            background-color: #f2dede;
            color: #f00;
            padding: 3px;
            border: 1px solid #f00;
            border-radius: 3px;
        }

        .auth-systems {
            margin-inline: auto;
            display: flex;
            flex-direction: column;
            gap: 20px;
            align-items: stretch;


            @media (min-width: @screen-sm-min) {
                width: 80%;
            }

            @media (min-width: @screen-md-min) {
                width: 60%;
            }

            :global {
                .auth-system {
                    &:not(:first-child) .login-system-panel-top-border {
                        border-top: 1px solid lightgray;
                    }
                }
            }
        }
    }
</style>